import React, { useState, useEffect } from 'react';
import { useMentorAuth } from '../contexts/MentorAuthContext';
import axios from 'axios';
import { toast } from 'react-toastify';
import { TrendingUp, Calendar, Clock, Users, BookOpen, Award, Target, BarChart3 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function MentorStats() {
  const { mentor, getToken } = useMentorAuth();
  const [stats, setStats] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const token = getToken();
      const [statsRes, sessionsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/mentor/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${BACKEND_URL}/api/mentor/sessions`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setStats(statsRes.data);
      setSessions(sessionsRes.data.sessions);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al cargar estadísticas');
    } finally {
      setIsLoading(false);
    }
  };

  const calculateEngagement = () => {
    if (!sessions.length) return 0;
    const completedSessions = sessions.filter(s => s.status === 'replay' || new Date(s.date) < new Date()).length;
    return Math.round((completedSessions / sessions.length) * 100);
  };

  const getUpcomingThisMonth = () => {
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    
    return sessions.filter(s => {
      const sessionDate = new Date(s.date);
      return sessionDate >= startOfMonth && sessionDate <= endOfMonth && s.status !== 'replay';
    }).length;
  };

  const getPilarDistribution = () => {
    const distribution = {};
    sessions.forEach(s => {
      distribution[s.pilar] = (distribution[s.pilar] || 0) + 1;
    });
    return Object.entries(distribution).sort((a, b) => b[1] - a[1]);
  };

  if (isLoading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <div className="text-white">Cargando estadísticas...</div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">Estadísticas y Desempeño</h1>
          <p className="text-gray-400">Análisis detallado de tu actividad como mentor</p>
        </div>

        {/* Main Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-gradient-to-br from-teal-500/20 to-teal-600/10 border border-teal-500/30 rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-teal-500/20 rounded-lg">
                <BookOpen className="text-teal-400" size={24} />
              </div>
              <TrendingUp className="text-teal-400" size={20} />
            </div>
            <p className="text-gray-400 text-sm mb-1">Total Sesiones</p>
            <p className="text-4xl font-bold text-white">{stats?.total_sessions || 0}</p>
            <p className="text-xs text-teal-400 mt-2">Todas las sesiones asignadas</p>
          </div>

          <div className="bg-gradient-to-br from-blue-500/20 to-blue-600/10 border border-blue-500/30 rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-blue-500/20 rounded-lg">
                <Calendar className="text-blue-400" size={24} />
              </div>
            </div>
            <p className="text-gray-400 text-sm mb-1">Este Mes</p>
            <p className="text-4xl font-bold text-white">{getUpcomingThisMonth()}</p>
            <p className="text-xs text-blue-400 mt-2">Sesiones programadas</p>
          </div>

          <div className="bg-gradient-to-br from-yellow-500/20 to-yellow-600/10 border border-yellow-500/30 rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-yellow-500/20 rounded-lg">
                <Clock className="text-yellow-400" size={24} />
              </div>
            </div>
            <p className="text-gray-400 text-sm mb-1">Horas Impartidas</p>
            <p className="text-4xl font-bold text-white">{stats?.total_hours || 0}h</p>
            <p className="text-xs text-yellow-400 mt-2">Tiempo total enseñando</p>
          </div>

          <div className="bg-gradient-to-br from-green-500/20 to-green-600/10 border border-green-500/30 rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-green-500/20 rounded-lg">
                <Users className="text-green-400" size={24} />
              </div>
            </div>
            <p className="text-gray-400 text-sm mb-1">Estudiantes</p>
            <p className="text-4xl font-bold text-white">{stats?.total_students || 0}</p>
            <p className="text-xs text-green-400 mt-2">Total de estudiantes</p>
          </div>
        </div>

        {/* Secondary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-black/30 backdrop-blur-xl border border-white/10 rounded-xl p-6">
            <div className="flex items-center space-x-3 mb-4">
              <div className="p-2 bg-purple-500/20 rounded-lg">
                <Target className="text-purple-400" size={20} />
              </div>
              <h3 className="text-lg font-semibold text-white">Tasa de Completitud</h3>
            </div>
            <div className="flex items-end space-x-2">
              <p className="text-3xl font-bold text-white">{calculateEngagement()}%</p>
              <p className="text-sm text-gray-400 mb-1">de sesiones</p>
            </div>
            <div className="mt-3 bg-black/30 rounded-full h-2">
              <div
                className="bg-gradient-to-r from-purple-500 to-purple-600 h-2 rounded-full transition-all"
                style={{ width: `${calculateEngagement()}%` }}
              />
            </div>
          </div>

          <div className="bg-black/30 backdrop-blur-xl border border-white/10 rounded-xl p-6">
            <div className="flex items-center space-x-3 mb-4">
              <div className="p-2 bg-orange-500/20 rounded-lg">
                <Calendar className="text-orange-400" size={20} />
              </div>
              <h3 className="text-lg font-semibold text-white">Próximas</h3>
            </div>
            <div className="flex items-end space-x-2">
              <p className="text-3xl font-bold text-white">{stats?.upcoming_sessions || 0}</p>
              <p className="text-sm text-gray-400 mb-1">sesiones</p>
            </div>
            <p className="text-xs text-orange-400 mt-2">En los próximos días</p>
          </div>

          <div className="bg-black/30 backdrop-blur-xl border border-white/10 rounded-xl p-6">
            <div className="flex items-center space-x-3 mb-4">
              <div className="p-2 bg-pink-500/20 rounded-lg">
                <Award className="text-pink-400" size={20} />
              </div>
              <h3 className="text-lg font-semibold text-white">Completadas</h3>
            </div>
            <div className="flex items-end space-x-2">
              <p className="text-3xl font-bold text-white">{stats?.completed_sessions || 0}</p>
              <p className="text-sm text-gray-400 mb-1">sesiones</p>
            </div>
            <p className="text-xs text-pink-400 mt-2">Sesiones finalizadas</p>
          </div>
        </div>

        {/* Distribution by Pilar */}
        <div className="bg-black/30 backdrop-blur-xl border border-white/10 rounded-xl p-6">
          <div className="flex items-center space-x-3 mb-6">
            <div className="p-2 bg-teal-500/20 rounded-lg">
              <BarChart3 className="text-teal-400" size={20} />
            </div>
            <h3 className="text-xl font-bold text-white">Distribución por Pilar</h3>
          </div>

          {getPilarDistribution().length === 0 ? (
            <p className="text-center text-gray-400 py-8">No hay datos para mostrar</p>
          ) : (
            <div className="space-y-4">
              {getPilarDistribution().map(([pilar, count], index) => {
                const percentage = Math.round((count / sessions.length) * 100);
                const colors = [
                  'from-teal-500 to-teal-600',
                  'from-blue-500 to-blue-600',
                  'from-purple-500 to-purple-600',
                  'from-pink-500 to-pink-600',
                  'from-orange-500 to-orange-600'
                ];
                const color = colors[index % colors.length];
                
                return (
                  <div key={pilar}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-white font-medium">{pilar}</span>
                      <span className="text-sm text-gray-400">{count} sesiones ({percentage}%)</span>
                    </div>
                    <div className="bg-black/30 rounded-full h-3">
                      <div
                        className={`bg-gradient-to-r ${color} h-3 rounded-full transition-all`}
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Performance Summary */}
        <div className="mt-8 bg-gradient-to-br from-teal-500/10 to-blue-500/10 border border-teal-500/20 rounded-xl p-6">
          <h3 className="text-xl font-bold text-white mb-4">Resumen de Desempeño</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div>
              <p className="text-gray-400 mb-1">Promedio horas/semana</p>
              <p className="text-2xl font-bold text-white">
                {stats?.total_hours ? (stats.total_hours / 4).toFixed(1) : 0}h
              </p>
            </div>
            <div>
              <p className="text-gray-400 mb-1">Duración promedio/sesión</p>
              <p className="text-2xl font-bold text-white">
                {sessions.length > 0 ? Math.round(sessions.reduce((acc, s) => acc + s.duration, 0) / sessions.length) : 0} min
              </p>
            </div>
            <div>
              <p className="text-gray-400 mb-1">Pilares impartidos</p>
              <p className="text-2xl font-bold text-white">
                {getPilarDistribution().length}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
